import {useContext} from "react"
import {AllDataContext} from "./LoggedinMainPage"
import {ADError, ADWarning} from "./alldatachecks"
import {SalesOrderCalcFields, SalesOrderLineCalcFields} from "./api/salesorder"
import {PurchaseOrderLineCalcFields} from "./api/purchaseorder"
import {WarehouseTransferLineCalcFields} from "./api/warehousetransfer"
import CalcFieldDoc from "./CalcFieldDoc"
import "./Home.scss"

function IssueMessage({issue} : {issue: ADError | ADWarning}) {
  return <div className="issue">
    <h3 className={`issue-${issue.level}`}>
      The <span className="check-name">{issue.name}</span> check has
      found {issue.count} {issue.level}{issue.count === 1 ? "" : "s"}
    </h3>
        <input type="checkbox" className="more-info" />
        <div className="more-info">
          {issue.extra_info}
        </div>
    {issue.formatted}
    </div>
}


export default function Home() {
  const allData = useContext(AllDataContext)

  const calcFieldsByTitle = {
    "Purchase Order Line": PurchaseOrderLineCalcFields,
    "Sales Order": SalesOrderCalcFields,
    "Sales Order Line": SalesOrderLineCalcFields,
    "Warehouse Transfer Line": WarehouseTransferLineCalcFields,
  }

  return <div className="Home">
    <h2>NoseCC: Stock & Backorders</h2>
    <details className="cheatsheet">
      <summary>Notes cheat sheet</summary>
      <p>
        This application makes uses of data stored in the <em>Notes</em> field of
        different objects in Exact (in some objects these fields are
        called <em>Remark</em>; in WarehouseTransferLine you have to use the description field, which also means you can only write 1 item in there).
      </p>
      <p>
        This field is read; all lines starting with <code>&gt;@@</code> (think:
        Nose-C-C) are assumed to
        be data used in this app (all other data is ignored). The line looks like
        this: <code>&gt;@@dataName: data</code> (so for
        instance: <code>&gt;@@shipping: ABC123, DEF456</code>).
      </p>
      <p>
        Any lines that start with <code>&gt;@@</code> and do not have this format,
        have a <em>dataName</em> that is not known for this object, or have an
        unexpected (or unparsable) value, result in an error.
      </p>
      <p>
        Each <em>dataName</em> will <em>either</em> be created by a human
        through the exact interface, <em>or</em> by the NoseCC application, not both
        (there is a small number of exceptions to this rule; in this case this is
        explicitly stated in the documentation).
        The <em>dataName</em>s that are created by the system, all have the
        suffix <code>-auto</code>, and MUST NOT ever be created, changed, or
        deleted through the Exact interface (doing so may break the NoseCC
        application, and recovery may be hard).
        It can be useful to <em>inspect</em> the value of these <em>dataName</em>s;
        for that purspose they are documented below.
      </p>
      <p>
        Each <em>dataName</em> expects the value to be in a specific format:
      </p>
      <dl>
        <dt>Yes/No</dt>
        <dd>
          Either <code>yes</code> or <code>no</code>.
          Case insensitive, so <code>YES</code> or <code>Yes</code> will
          also work.
        </dd>
        <dt>NonNegInt</dt>
        <dd>
          A positive integer (whole number) or <code>0</code>.
          Negative number are not allowed. Numbers with decimal points are not
          allowed either. Do not use numbers larger than 1,000,000 (1 million).
        </dd>
        <dt>Date</dt>
        <dd>Date in YYYY-MM-DD format (e.g. <code>2022-05-03</code>)</dd>
        <dt>CSV-list</dt>
        <dd>
          List of comma separated values (CSV).
          Multiple strings, separated by commas.
          Space after the comma is optional.
          (e.g. <code>ABC123, DEF456</code>)
        </dd>
        <dt>SO-L</dt>
        <dd>
          Points to a Sales Order Line.
          In order to point to Sales Order number 123, line 5,
          the value must be: <code>SO:123(5)</code>
        </dd>
        <dt>PO-L</dt>
        <dd>
          Points to a Purchase Order Line.
          In order to point to Purchase Order number 123, line 5,
          the value must be: <code>PO:123(5)</code>
        </dd>
        <dt>WT-L</dt>
        <dd>
          Points to a Warehouse Transfer Line.
          In order to point to Warehouse Transfer number 123, line 5,
          the value must be: <code>WT:123(5)</code>
        </dd>
      </dl>

      <table className="cheatsheet">
        <thead>
          <tr>
            <th>dataName</th>
            <th>data format</th>
            <th>default value</th>
            <th>description & example</th>
          </tr>
        </thead>

        {Object.entries(calcFieldsByTitle).map(
          ([title, calcFields]) => <tbody key={title}>
            <tr><th colSpan={4}>{title}</th></tr>
            {Object.entries(calcFields).map(([name, calcField]) =>
            <CalcFieldDoc key={name} name={name} calcField={calcField} />
            )}
          </tbody>)
        }
      </table>
    </details>

    {allData.errors.length === 0 ?
      <h3 className="no-errors">There were no errors detected in the data</h3> :
      <>
        <h3 className="errors">Several errors were detected in the data</h3>
        <p>
          Considering that several errors were detected in the data, it is strongly
          advised that these are first fixed.
          If they do not get fixed, <em>any overviews you generate in this
            app may be wrong</em>.
          In addition, if you use for example the SO tool, <em>it may reschedule
            orders incorrectly, send incorrect updates to clients and interrupt
            normal operations</em>.
        </p>
        <p>
          Unlike warnings, <em>errors should never be ignored</em>.
          If there are errors shown which you think should be ignored in your
          situation (even for a day), <em>please report this</em> (because either
          it's a bug in the application, or something else is wrong).
        </p>
        <p>
          After fixing the errors in Exact, reload this page.
        </p>
        {allData.errors.map(
          error => <IssueMessage key={error.name} issue={error} />)}
      </>
    }
    {allData.warnings.length === 0 ?
      <h3 className="no-warnings">There were no warnings detected in the data</h3> :
      <>
        <h3 className="warnings">Several warnings were detected in the data</h3>
        <p>
          Warnings are things you should take a good look at.
          Most likely you should fix this list, or at least make sure you
          understand each of the items on this list why it is considered a
          warning.
        </p>
        <p>
          You can however safely use the application without fixing these warnings.
          The SO tool may even fix some of these warnings.
        </p>
        <p>
        </p>
        {allData.warnings.map(
          warning => <IssueMessage key={warning.name} issue={warning} />)}
      </>
    }
    {(Object.keys(allData) as (keyof typeof allData)[])
      .filter(key => Array.isArray(allData[key]))
      .map(key => <input type="hidden" name={`cy-alldata-nr-${key}`} key={key}
            value={(allData[key] as any[]).length} />
    )}
  </div>
}
